import React, { useState, useEffect, useCallback } from 'react';
import { Send, CheckCircle, AlertCircle, FileText, Plus, Edit2, Trash2, X, Save, Star, RefreshCw, UserPlus, TrendingUp, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const iconMap = {
  'star': <Star className="h-5 w-5" />,
  'refresh': <RefreshCw className="h-5 w-5" />,
  'user-plus': <UserPlus className="h-5 w-5" />,
  'trending-up': <TrendingUp className="h-5 w-5" />,
  'shopping-cart': <ShoppingCart className="h-5 w-5" />,
  'file-text': <FileText className="h-5 w-5" />,
  'send': <Send className="h-5 w-5" />,
};

const colorOptions = ['#FFB800', '#00FFFF', '#00FF88', '#F7931E', '#FF6B35', '#FF3B5C', '#9B59B6'];

const EmailMarketing = () => {
  const [formData, setFormData] = useState({ subject: '', htmlContent: '', target: 'all_clients' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorForm, setEditorForm] = useState({ name: '', subject: '', content: '', icon: 'file-text', color: '#FF6B35' });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/admin/email-templates');
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false); setSending(true);
    try {
      await api.post('/admin/send-email', { subject: formData.subject, html_content: formData.htmlContent, target: formData.target });
      setSuccess(true);
      setFormData({ subject: '', htmlContent: '', target: 'all_clients' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  const applyTemplate = (tpl) => {
    setFormData({ ...formData, subject: tpl.subject, htmlContent: tpl.content });
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setEditorForm({ name: '', subject: '', content: '', icon: 'file-text', color: '#FF6B35' });
    setShowEditor(true);
  };

  const openEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setEditorForm({ name: tpl.name, subject: tpl.subject, content: tpl.content, icon: tpl.icon || 'file-text', color: tpl.color || '#FF6B35' });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    if (!editorForm.name || !editorForm.subject) return;
    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/admin/email-templates/${editingTemplate.id}`, editorForm);
      } else {
        await api.post('/admin/email-templates', editorForm);
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await api.delete(`/admin/email-templates/${id}`);
      fetchTemplates();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-8" data-testid="email-marketing">
      {/* Templates Section */}
      <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-[#FF6B35]" />
            <h3 className="text-2xl font-bold text-white">Templates email</h3>
          </div>
          <button
            onClick={openNewTemplate}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-[#FF6B35]/30 transition-all"
            data-testid="create-template-btn"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau template</span>
          </button>
        </div>

        {loadingTemplates ? (
          <div className="text-center py-8 text-[#6B6B6E]">Chargement...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-[#6B6B6E]">Aucun template</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                className="bg-[#222226] p-5 rounded-lg border transition-all hover:shadow-lg group"
                style={{ borderColor: `${tpl.color || '#3A3A3D'}40` }}
                data-testid={`template-card-${tpl.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div style={{ color: tpl.color || '#FF6B35' }}>
                      {iconMap[tpl.icon] || <FileText className="h-5 w-5" />}
                    </div>
                    <h4 className="text-white font-bold text-sm">{tpl.name}</h4>
                  </div>
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditTemplate(tpl)}
                      className="p-1.5 rounded-lg hover:bg-[#3A3A3D] text-[#00FFFF] transition-colors"
                      title="Modifier"
                      data-testid={`edit-template-${tpl.id}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(tpl.id)}
                      className="p-1.5 rounded-lg hover:bg-[#3A3A3D] text-[#FF3B5C] transition-colors"
                      title="Supprimer"
                      data-testid={`delete-template-${tpl.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-[#B8B8BA] text-xs mb-3 line-clamp-2">{tpl.subject}</p>
                <button
                  onClick={() => applyTemplate(tpl)}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors"
                  style={{ color: tpl.color || '#00FFFF', borderColor: `${tpl.color || '#00FFFF'}50` }}
                  data-testid={`use-template-${tpl.id}`}
                >
                  Utiliser
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-lg metallic-border cyber-glow"
      >
        <div className="flex items-center space-x-3 mb-6">
          <Send className="h-8 w-8 text-[#00FFFF]" />
          <h2 className="text-3xl font-bold text-white">Envoi d'emails</h2>
        </div>

        {success && (
          <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-6 flex items-start space-x-3" data-testid="success-message">
            <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0 mt-0.5" />
            <span className="text-[#00FF88] text-sm">Emails envoy&eacute;s avec succ&egrave;s !</span>
          </div>
        )}
        {error && (
          <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-6 flex items-start space-x-3" data-testid="error-message">
            <AlertCircle className="h-5 w-5 text-[#FF3B5C] flex-shrink-0 mt-0.5" />
            <span className="text-[#FF3B5C] text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[#E8E8E8] font-semibold mb-3">Cible</label>
            <select
              value={formData.target}
              onChange={(e) => setFormData({ ...formData, target: e.target.value })}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              data-testid="target-select"
            >
              <option value="all_clients">Tous les clients</option>
              <option value="clients_no_subscription">Clients sans abonnement</option>
              <option value="clients_with_subscription">Clients avec abonnement actif</option>
              <option value="clients_expired">Clients avec abonnement expiré</option>
              <option value="all_vendors">Tous les vendeurs</option>
              <option value="all_users">Tous les utilisateurs</option>
            </select>
          </div>
          <div>
            <label className="block text-[#E8E8E8] font-semibold mb-3">Sujet</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
              placeholder="Sujet de l'email"
              required
              data-testid="subject-input"
            />
          </div>
          <div>
            <label className="block text-[#E8E8E8] font-semibold mb-3">Contenu HTML</label>
            <textarea
              value={formData.htmlContent}
              onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
              rows="10"
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono text-sm"
              placeholder="<h1>Titre</h1><p>Contenu...</p>"
              required
              data-testid="content-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full btn-primary cyber-glow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            data-testid="send-email-button"
          >
            <Send className="h-5 w-5" />
            <span>{sending ? 'Envoi en cours...' : 'Envoyer les emails'}</span>
          </button>
        </form>
      </motion.div>

      {/* Template Editor Modal */}
      <AnimatePresence>
        {showEditor && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowEditor(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              data-testid="template-editor-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
                </h2>
                <button onClick={() => setShowEditor(false)} className="text-[#B8B8BA] hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-[#E8E8E8] text-sm font-medium mb-2">Nom du template</label>
                  <input
                    type="text"
                    value={editorForm.name}
                    onChange={(e) => setEditorForm({ ...editorForm, name: e.target.value })}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="Ex: Promotion de Noël"
                    data-testid="editor-name"
                  />
                </div>

                <div>
                  <label className="block text-[#E8E8E8] text-sm font-medium mb-2">Sujet de l'email</label>
                  <input
                    type="text"
                    value={editorForm.subject}
                    onChange={(e) => setEditorForm({ ...editorForm, subject: e.target.value })}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                    placeholder="Sujet affiché dans la boîte de réception"
                    data-testid="editor-subject"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#E8E8E8] text-sm font-medium mb-2">Ic&ocirc;ne</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(iconMap).map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setEditorForm({ ...editorForm, icon: key })}
                          className={`p-2 rounded-lg border transition-colors ${editorForm.icon === key ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'border-[#3A3A3D] text-[#6B6B6E] hover:text-white'}`}
                        >
                          {iconMap[key]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#E8E8E8] text-sm font-medium mb-2">Couleur</label>
                    <div className="flex flex-wrap gap-2">
                      {colorOptions.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditorForm({ ...editorForm, color: c })}
                          className={`w-8 h-8 rounded-lg border-2 transition-transform ${editorForm.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[#E8E8E8] text-sm font-medium mb-2">Contenu HTML</label>
                  <textarea
                    value={editorForm.content}
                    onChange={(e) => setEditorForm({ ...editorForm, content: e.target.value })}
                    rows="12"
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono text-sm"
                    placeholder="<h2>Titre</h2><p>Votre contenu ici...</p>"
                    data-testid="editor-content"
                  />
                </div>

                <div className="flex items-center space-x-3 pt-2">
                  <button
                    onClick={handleSaveTemplate}
                    disabled={saving || !editorForm.name || !editorForm.subject}
                    className="flex-1 btn-primary py-3 disabled:opacity-50 flex items-center justify-center space-x-2"
                    data-testid="save-template-btn"
                  >
                    <Save className="h-5 w-5" />
                    <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                  </button>
                  <button
                    onClick={() => setShowEditor(false)}
                    className="px-6 py-3 bg-[#3A3A3D] text-white rounded-lg hover:bg-[#4A4A4D] transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmailMarketing;
